import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { useLandingData } from "@/context/LandingDataContext";

export const Testimonials = () => {
  const { content } = useLandingData();
  const testimonialsSection = content?.testimonialsSection || {
    overline: "— Bukti Nyata",
    title: "Cerita dari mereka yang",
    italicTitle: "telah percaya."
  };
  const testimonials = content?.testimonials || [];

  const [index, setIndex] = useState(0);
  const t = testimonials[index] || { quote: "", name: "", role: "" };

  const prev = () =>
    setIndex((i) => (i - 1 + testimonials.length) % testimonials.length);
  const next = () => setIndex((i) => (i + 1) % testimonials.length);

  if (testimonials.length === 0) return null;

  return (
    <section
      data-testid="testimonials-section"
      className="relative bg-[#F5F2EB] py-24 md:py-36 overflow-hidden"
    >
      <div className="absolute inset-0 grain-overlay pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center">
        <p className="text-xs tracking-[0.4em] uppercase text-[#8A6E52] mb-8">
          {testimonialsSection.overline}
        </p>
        <h2
          data-testid="testimonials-title"
          className="font-serif-display text-4xl md:text-6xl leading-[1.05] tracking-tight text-[#1A1A1A] font-light"
        >
          {testimonialsSection.title}
          <br />
          <em className="italic font-normal text-[#8A6E52]">{testimonialsSection.italicTitle}</em>
        </h2>

        <div className="relative mt-16 md:mt-20 min-h-[280px] md:min-h-[240px] flex items-center justify-center">
          <Quote className="absolute -top-2 left-1/2 -translate-x-1/2 h-10 w-10 text-[#BCA38F]/40 rotate-180" />

          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`testimonial-${index}`}
              className="max-w-3xl px-4 md:px-12"
            >
              <p className="font-serif-display italic text-2xl md:text-3xl leading-relaxed text-[#1A1A1A] font-light">
                &ldquo;{t.quote}&rdquo;
              </p>
              <div className="mt-10">
                <p
                  data-testid={`testimonial-name-${index}`}
                  className="text-sm tracking-[0.28em] uppercase text-[#1A1A1A]"
                >
                  {t.name}
                </p>
                <p className="mt-2 text-xs text-[#5C5C5C]">{t.role}</p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-12 flex items-center justify-center gap-6">
          <button
            onClick={prev}
            data-testid="testimonial-prev"
            aria-label="Sebelumnya"
            className="h-12 w-12 rounded-full border border-[#1A1A1A]/20 flex items-center justify-center text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FDFBF7] transition-all duration-500"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                data-testid={`testimonial-dot-${i}`}
                aria-label={`Testimoni ${i + 1}`}
                className={`h-1.5 transition-all duration-500 rounded-full ${
                  i === index ? "w-8 bg-[#1A1A1A]" : "w-2 bg-[#1A1A1A]/25"
                }`}
              />
            ))}
          </div>
          <button
            onClick={next}
            data-testid="testimonial-next"
            aria-label="Selanjutnya"
            className="h-12 w-12 rounded-full border border-[#1A1A1A]/20 flex items-center justify-center text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FDFBF7] transition-all duration-500"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
};
